import { create } from 'zustand'
import {
  collection, doc, addDoc, updateDoc, getDocs, query, where, serverTimestamp, setDoc,
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuthStore } from './auth'
import type { Timestamp, FieldValue } from 'firebase/firestore'

type FirestoreTimestamp = Timestamp | FieldValue | null

interface ApplicationData {
  orgName: string
  orgType: OrgType
  contactName: string
  phone: string
  city: string
  registrationNumber: string
  clientCount: ClientCount | ''
  website: string
  description: string
}

export type OrgType = 'therapy_center' | 'special_ed_school' | 'hospital_clinic' | 'ngo' | 'other'
export type ClientCount = 'under_10' | '10_to_50' | '51_to_100' | 'over_100'

export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  therapy_center:    'Therapy Center',
  special_ed_school: 'Special Education School',
  hospital_clinic:   'Hospital / Clinic',
  ngo:               'NGO / Foundation',
  other:             'Other',
}

export const CLIENT_COUNT_LABELS: Record<ClientCount, string> = {
  under_10:    'Under 10',
  '10_to_50':  '10–50',
  '51_to_100': '51–100',
  over_100:    'Over 100',
}

export interface OrgApplication {
  id: string
  orgName: string
  orgType: OrgType
  contactName: string
  contactEmail: string
  phone: string
  city: string
  registrationNumber: string
  clientCount: ClientCount | ''
  applicantUid: string
  website: string
  description: string
  status: 'pending' | 'approved' | 'rejected'
  submittedAt: FirestoreTimestamp
  updatedAt?: FirestoreTimestamp
  reviewedAt?: FirestoreTimestamp
  reviewedBy?: string
  rejectionReason?: string
}

export interface ApprovedOrg {
  id: string
  name: string
  type: OrgType
  contactName: string
  contactEmail: string
  contactPhone?: string
  website?: string
  ownerUid: string
  createdAt: FirestoreTimestamp
}

interface OrgProvisioningState {
  applications: OrgApplication[]
  myApplication: OrgApplication | null
  approvedOrgs: ApprovedOrg[]
  loading: boolean

  fetchMyApplication: () => Promise<void>
  submitApplication: (data: ApplicationData) => Promise<void>
  updateApplication: (data: ApplicationData) => Promise<void>
  fetchApplications: () => Promise<void>
  fetchApprovedOrgs: () => Promise<void>
  updateOrg: (orgId: string, data: { contactName: string; contactPhone: string; website: string }) => Promise<void>
}

export const useOrgProvisioningStore = create<OrgProvisioningState>()((set, get) => ({
  applications: [],
  myApplication: null,
  approvedOrgs: [],
  loading: false,

  async fetchMyApplication() {
    const auth = useAuthStore.getState()
    const uid = auth.uid()
    if (!uid) return
    set({ loading: true })
    try {
      const q = query(
        collection(db, 'org_applications'),
        where('applicantUid', '==', uid),
        where('status', '==', 'pending'),
      )
      const snap = await getDocs(q)
      set({
        myApplication: snap.empty
          ? null
          : { id: snap.docs[0].id, ...(snap.docs[0].data() as Omit<OrgApplication, 'id'>) },
      })
    } catch (err) {
      console.warn('[orgProvisioning] fetchMyApplication failed:', err)
      set({ myApplication: null })
    } finally {
      set({ loading: false })
    }
  },

  async submitApplication(data) {
    const auth = useAuthStore.getState()
    const uid = auth.uid()
    if (!uid) throw new Error('Not authenticated')
    const payload = {
      orgName: data.orgName.trim(),
      orgType: data.orgType,
      contactName: data.contactName.trim(),
      contactEmail: auth.user?.email ?? '',
      phone: data.phone.trim(),
      city: data.city.trim(),
      registrationNumber: data.registrationNumber.trim(),
      clientCount: data.clientCount,
      applicantUid: uid,
      website: data.website.trim(),
      description: data.description.trim(),
      status: 'pending' as const,
      submittedAt: serverTimestamp(),
    }
    try {
      const docRef = await addDoc(collection(db, 'org_applications'), payload)
      set({ myApplication: { id: docRef.id, ...payload, submittedAt: null } })
      await setDoc(doc(db, 'users', uid), { orgApplicationStatus: 'pending' }, { merge: true })
    } catch (err) {
      console.warn('[orgProvisioning] submitApplication failed:', err)
      throw err
    }
  },

  async updateApplication(data) {
    const current = get().myApplication
    if (!current) throw new Error('No pending application')
    const updates = {
      orgName: data.orgName.trim(),
      orgType: data.orgType,
      contactName: data.contactName.trim(),
      phone: data.phone.trim(),
      city: data.city.trim(),
      registrationNumber: data.registrationNumber.trim(),
      clientCount: data.clientCount,
      website: data.website.trim(),
      description: data.description.trim(),
      updatedAt: serverTimestamp(),
    }
    try {
      await updateDoc(doc(db, 'org_applications', current.id), updates)
      set({ myApplication: { ...current, ...updates, updatedAt: null } })
    } catch (err) {
      console.warn('[orgProvisioning] updateApplication failed:', err)
      throw err
    }
  },

  async fetchApplications() {
    set({ loading: true })
    try {
      const q = query(collection(db, 'org_applications'), where('status', '==', 'pending'))
      const snap = await getDocs(q)
      set({ applications: snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<OrgApplication, 'id'>) })) })
    } catch (err) {
      console.warn('[orgProvisioning] fetchApplications failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  async fetchApprovedOrgs() {
    set({ loading: true })
    try {
      const snap = await getDocs(collection(db, 'org_applications'))
      set({
        approvedOrgs: snap.docs
          .filter(d => d.data().status === 'approved')
          .map(d => ({ id: d.id, ...(d.data() as Omit<ApprovedOrg, 'id'>) })),
      })
    } catch (err) {
      console.warn('[orgProvisioning] fetchApprovedOrgs failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  async updateOrg(orgId, data) {
    try {
      const patch = {
        contactName: data.contactName,
        contactPhone: data.contactPhone,
        website: data.website,
      }
      // Application row (legacy Settings path) + live org doc (Beacon Ops
      // mirrors orgs/{id} → Laravel orgs; application-only writes never
      // created the Filament Org row needed for panel login).
      await updateDoc(doc(db, 'org_applications', orgId), patch)
      try {
        await updateDoc(doc(db, 'orgs', orgId), patch)
      } catch (orgErr) {
        // Org doc may lag approve; application write already succeeded.
        console.warn('[orgProvisioning] updateOrg orgs/ patch failed:', orgErr)
      }
      set(s => ({
        approvedOrgs: s.approvedOrgs.map(o =>
          o.id === orgId ? { ...o, ...data } : o
        ),
      }))
    } catch (err) {
      console.warn('[orgProvisioning] updateOrg failed:', err)
      throw err
    }
  },
}))
